/**
 * Owner: Masa Bayu
 * File: PrivateAuthorMobileHome.tsx
 * Date:Time: 2026-10-23 09:25
 * Description: Halaman utama mudah alih PRIVAT §467-C — topbar (logo + carian
 *   boleh-timbul → /search?q=…, hamburger → laci 9 navigasi), hero biru (avatar
 *   inisial + nama + peran + tahun dari sesi hub + Edit Profil + butang QXEditor
 *   §421 + ikon e-mel sokongan), kad statistik 4 sel (terbitan NYATA; pengikut
 *   dan rating "tiada data" kerana hub belum ada sumber) dan kad Prestasi/
 *   Pintasan/Buku/Notifikasi daripada model yang sama dengan desktop.
 *   §462 — loceng header dibuang: notifikasi sudah ada sebagai tab bell dalam
 *   bar bawah (QxBottomNav), jadi satu laluan sahaja.
 *   Dibuang bersama dataset demo: potret stok, lencana verified, kutipan orang
 *   lain, lokasi tetap dan 48.5K/4.8 berlabel DEMO.
 */

import { useState } from 'react';
import type { FormEvent, ReactElement, ReactNode } from 'react';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import { QX_TAGLINE } from '../../brand/brand-assets';
import type { MarketplaceLocaleProfile } from '../../home/marketplace/marketplace-locale';
import { STUDIO_ENTRY } from '../author-production-identity';
import type { PrivateAuthorLabels } from './private-author-labels';
import type { AuthorDashboardModel } from './private-author-view';
import type {
  PrivateAuthorMobileLabels,
  PrivateMobileViewId,
} from './private-author-mobile-labels';
import {
  MobileBooksRail,
  MobileNotifications,
  MobilePrestasi,
  MobileShortcuts,
} from './PrivateAuthorMobileHomeParts';

function Stroke({
  children,
  size = 20,
}: {
  readonly children: ReactNode;
  readonly size?: number;
}): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export interface PrivateAuthorMobileHomeProps {
  readonly onNavigate: (path: string) => void;
  readonly openView: (view: PrivateMobileViewId) => void;
  readonly profile: MarketplaceLocaleProfile;
  readonly la: PrivateAuthorLabels;
  readonly ml: PrivateAuthorMobileLabels;
  readonly model: AuthorDashboardModel;
}

export function PrivateAuthorMobileHome({
  onNavigate,
  openView,
  profile,
  la,
  ml,
  model,
}: PrivateAuthorMobileHomeProps): ReactElement {
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [query, setQuery] = useState('');

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setSearchOpen(false);
    const needle = query.trim();
    onNavigate(needle.length === 0 ? '/search' : `/search?q=${encodeURIComponent(needle)}`);
  };

  type DrawerAction =
    | { readonly kind: 'run'; readonly run: () => void }
    | { readonly kind: 'link'; readonly href: string };
  const drawerItems: readonly {
    readonly label: string;
    readonly icon: ReactNode;
    readonly action: DrawerAction;
  }[] = [
    {
      label: la.nav.dashboard,
      icon: (
        <Stroke size={17}>
          <path d="M3 11l9-7 9 7M5 10v10h14V10" />
        </Stroke>
      ),
      action: {
        kind: 'run',
        run: () => {
          openView('home');
        },
      },
    },
    {
      label: ml.bottom.buku,
      icon: (
        <Stroke size={17}>
          <path d="M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2zM9 3v18" />
        </Stroke>
      ),
      action: {
        kind: 'run',
        run: () => {
          openView('buku');
        },
      },
    },
    {
      label: la.nav.createEbook,
      icon: (
        <Stroke size={17}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v8M8 12h8" />
        </Stroke>
      ),
      action: {
        kind: 'run',
        run: () => {
          onNavigate(STUDIO_ENTRY);
        },
      },
    },
    {
      label: la.nav.manuscripts,
      icon: (
        <Stroke size={17}>
          <path d="M7 3h8l4 4v14H7zM15 3v4h4" />
        </Stroke>
      ),
      action: {
        kind: 'run',
        run: () => {
          onNavigate(STUDIO_ENTRY);
        },
      },
    },
    {
      label: la.nav.sales,
      icon: (
        <Stroke size={17}>
          <path d="M4 20V8M10 20V4M16 20v-8M21 20H3" />
        </Stroke>
      ),
      action: {
        kind: 'run',
        run: () => {
          openView('sales');
        },
      },
    },
    {
      label: la.nav.promotions,
      icon: (
        <Stroke size={17}>
          <path d="M4 11l12-6v14L4 13zM4 11v3M18 8a3 3 0 0 1 0 6" />
        </Stroke>
      ),
      action: {
        kind: 'run',
        run: () => {
          onNavigate('/author/storefront');
        },
      },
    },
    {
      label: la.nav.reports,
      icon: (
        <Stroke size={17}>
          <path d="M5 21V9l5-5 9 3v13zM9 13h6M9 17h6" />
        </Stroke>
      ),
      action: {
        kind: 'run',
        run: () => {
          openView('analytics');
        },
      },
    },
    {
      label: la.nav.profile,
      icon: (
        <Stroke size={17}>
          <circle cx="12" cy="8" r="3.6" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </Stroke>
      ),
      action: {
        kind: 'run',
        run: () => {
          onNavigate('/author/settings');
        },
      },
    },
    {
      label: la.nav.help,
      icon: (
        <Stroke size={17}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.5 9.5A2.5 2.5 0 1 1 12 13v1.5M12 17.5v.5" />
        </Stroke>
      ),
      action: { kind: 'link', href: 'mailto:support@qxread.com' },
    },
  ];

  return (
    <>
      <header className="qxpm__topbar">
        <div className="qxpm__topbar-row">
          <button
            type="button"
            className="qxpm__menu"
            aria-label={ml.menuAria}
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <Stroke size={22}>
              <path d="M4 7h16M4 12h16M4 17h16" />
            </Stroke>
          </button>
          <span className="qxpm__brand">
            <img src="/brand/qx.png" alt="QX" width="26" height="26" />
            <span className="qxpm__brand-text">
              <strong>{QX_TAGLINE.top}</strong>
              <small>{QX_TAGLINE.bottom}</small>
            </span>
          </span>
          <span className="qxpm__topbar-actions">
            <button
              type="button"
              className="qxpm__iconbtn"
              aria-label={ml.searchAria}
              aria-expanded={searchOpen}
              onClick={() => {
                setSearchOpen((v) => !v);
              }}
            >
              <Stroke>
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-4.2-4.2" />
              </Stroke>
            </button>
          </span>
        </div>
        {searchOpen ? (
          <form
            className="qxpm__search"
            role="search"
            onSubmit={submit}
            aria-label={la.searchSubmit}
          >
            <input
              type="search"
              value={query}
              placeholder={la.searchPlaceholder}
              aria-label={ml.searchAria}
              autoFocus
              onChange={(event) => {
                setQuery(event.target.value);
              }}
            />
          </form>
        ) : null}
      </header>

      {drawerOpen ? (
        <div className="qxpm__drawer-root">
          <button
            type="button"
            className="qxpm__scrim"
            aria-label={ml.menuCloseAria}
            onClick={() => {
              setDrawerOpen(false);
            }}
          />
          <aside className="qxpm__drawer" aria-label={la.navAria}>
            <div className="qxpm__drawer-head">
              <img src="/brand/qx.png" alt="" width="26" height="26" />
              <span className="qxpm__brand-text">
                <strong>{QX_TAGLINE.top}</strong>
                <small>{QX_TAGLINE.bottom}</small>
              </span>
              <button
                type="button"
                className="qxpm__iconbtn"
                aria-label={ml.menuCloseAria}
                onClick={() => {
                  setDrawerOpen(false);
                }}
              >
                <Stroke size={18}>
                  <path d="M6 6l12 12M18 6L6 18" />
                </Stroke>
              </button>
            </div>
            <nav className="qxpm__drawer-nav">
              {drawerItems.map((item) => {
                const action = item.action;
                if (action.kind === 'link') {
                  return (
                    <a
                      key={item.label}
                      className="qxpm__drawer-item"
                      href={action.href}
                      {...(action.href.startsWith('http')
                        ? { target: '_blank', rel: 'noreferrer' }
                        : {})}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </a>
                  );
                }
                return (
                  <button
                    key={item.label}
                    type="button"
                    className="qxpm__drawer-item"
                    onClick={() => {
                      setDrawerOpen(false);
                      action.run();
                    }}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                    <em aria-hidden="true">
                      <LandingIcon name="chevronRight" size={12} />
                    </em>
                  </button>
                );
              })}
            </nav>
            <p className="qxpm__drawer-copy">{la.copyright}</p>
          </aside>
        </div>
      ) : null}

      <section className="qxpm__hero">
        <div className="qxpm__hero-top">
          <span className="qxpm__avatar qxpm__avatar--txt" aria-hidden="true">
            {model.initials}
          </span>
          <div className="qxpm__hero-id">
            <h1 className="qxpm__name">{model.name}</h1>
            <p className="qxpm__role">
              {la.role}
              {model.sinceYear === null ? '' : ` · ${la.since} ${model.sinceYear}`}
            </p>
            {model.email === null ? null : <p className="qxpm__mail">{model.email}</p>}
          </div>
        </div>
        <div className="qxpm__hero-actions">
          <button
            type="button"
            className="qxpm__edit"
            onClick={() => {
              onNavigate('/author/profile');
            }}
          >
            <Stroke size={15}>
              <path d="M4 20h4L20 8l-4-4L4 16z" />
            </Stroke>
            {la.editProfile}
          </button>
          <button
            type="button"
            className="qxpm__edit qxpm__editor"
            onClick={() => {
              onNavigate(STUDIO_ENTRY);
            }}
            aria-label={la.editorButtonAria}
            data-qxeditor-entry="author-dashboard-mobile"
          >
            <Stroke size={15}>
              <path d="M11 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2v-5" />
              <path d="M18.5 2.5a2.1 2.1 0 013 3L12 15l-3.5 1 1-3.5z" />
            </Stroke>
            {la.editorButton}
          </button>
          <a className="qxpm__social" aria-label={la.chatAria} href="mailto:support@qxread.com">
            <Stroke size={15}>
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </Stroke>
          </a>
        </div>
      </section>

      <div className="qxpm__stats">
        <div className="qxpm__stat">
          <strong>{String(model.booksPublished)}</strong>
          <em>{la.statBooks}</em>
        </div>
        <div className="qxpm__stat">
          <strong>{model.noData}</strong>
          <em>{la.statFollowers}</em>
        </div>
        <div className="qxpm__stat">
          <strong>{model.noData}</strong>
          <em>{la.statRating}</em>
        </div>
        <button
          type="button"
          className="qxpm__stat qxpm__stat--link"
          onClick={() => {
            onNavigate(model.profilePath);
          }}
        >
          <strong>{ml.statDetail}</strong>
          <em aria-hidden="true">
            <LandingIcon name="chevronRight" size={12} />
          </em>
        </button>
      </div>

      <div className="qxpm__body">
        <MobilePrestasi la={la} ml={ml} model={model} />
        <MobileShortcuts onNavigate={onNavigate} la={la} ml={ml} />
        <MobileBooksRail
          onNavigate={onNavigate}
          openView={openView}
          profile={profile}
          la={la}
          ml={ml}
          model={model}
        />
        <MobileNotifications onNavigate={onNavigate} la={la} ml={ml} model={model} />
      </div>
    </>
  );
}
